import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, MessageCircle, Sparkles } from "lucide-react";
import api, { WHATSAPP, resolveImg } from "../lib/api";
import { useSeo } from "../lib/seo";
import ProductCard from "../components/ProductCard";
import InstaFeed from "../components/InstaFeed";

const HERO =
  "https://images.unsplash.com/photo-1704040686428-7534b262d0d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CAT_IMG = {
  "wooden-trays": "https://images.unsplash.com/photo-1783508905246-700954947ffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "ceramic": "https://images.unsplash.com/photo-1577576308052-4c4f724ce7cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "cushion-covers": "https://images.unsplash.com/photo-1531877025030-f7696a50770f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "bedsheets": "https://images.unsplash.com/photo-1564019472231-4586c552dc27?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "sofa-covers": "https://images.unsplash.com/photo-1777980813395-9387e741b9f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "dining-covers": "https://images.unsplash.com/photo-1523658783928-e8287dea21cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "table-mats": "https://images.unsplash.com/photo-1688350526777-012623237ae2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "table-runners": "https://images.unsplash.com/photo-1685122121708-3f8bd945b5f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "wooden-platters": "/api/files/aethel-decor/products/3c23959a-0976-4151-8465-e2868dd4d4fb.jpg",
};
const CAT_IMG_FALLBACK = "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Home() {
  useSeo({});
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[82vh]">
          <div className="lg:col-span-5 flex flex-col justify-center px-5 sm:px-10 lg:px-14 py-16 order-2 lg:order-1">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.25em] text-accent font-semibold"
            >
              Beyond Decor · Simply Elevated
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-primary font-medium"
            >
              Quiet luxury for the<br />
              <span className="italic">everyday</span> home.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground font-light max-w-md leading-relaxed"
            >
              Serving trays, hand-finished ceramics, sofa & cushion covers,
              pure-cotton bedsheets and table linen — curated to bring warmth and
              calm into your rooms.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/shop"
                className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide hover:gap-3 transition-all"
                data-testid="hero-shop-btn"
              >
                Shop the Collection
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`https://wa.me/${WHATSAPP}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-primary/30 text-primary px-8 py-4 text-sm tracking-wide hover:bg-secondary transition-colors"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={18} /> Order on WhatsApp
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-7 relative order-1 lg:order-2 min-h-[45vh] lg:min-h-full overflow-hidden">
            <motion.img
              initial={{ scale: 1.08, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              src={HERO}
              alt="Elegant home décor setting"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {[
            { icon: Truck, t: "Free shipping", s: "Across India, on every order" },
            { icon: ShieldCheck, t: "Pay your way", s: "Online payment or Cash on Delivery" },
            { icon: MessageCircle, t: "Real people", s: "Chat with us on WhatsApp" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-4 py-6 sm:px-8">
              <f.icon size={24} className="text-primary shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-foreground">{f.t}</p>
                <p className="text-sm text-muted-foreground">{f.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Browse</p>
            <h2 className="mt-3 text-3xl sm:text-4xl text-primary font-medium">Shop by category</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm text-primary link-underline">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-7">
          {cats.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            >
              <Link
                to={`/shop?category=${c.slug}`}
                className="luxury-card group block overflow-hidden"
                data-testid={`home-cat-${c.slug}`}
              >
                <div className="overflow-hidden aspect-[3/4] rounded-t-[20px]">
                  <img
                    src={resolveImg(CAT_IMG[c.slug] || CAT_IMG_FALLBACK)}
                    alt={c.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="px-4 py-4 text-center">
                  <h3 className="font-display text-sm sm:text-base font-semibold text-primary uppercase tracking-wide leading-tight">{c.name}</h3>
                  <p className="mt-1.5 text-xs text-muted-foreground group-hover:text-primary transition-colors inline-flex items-center gap-1">
                    Explore Collection <ArrowRight size={12} />
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Curated</p>
          <h2 className="mt-3 text-3xl sm:text-4xl text-primary font-medium">Pieces we love</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Style Quiz CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="home-quiz-cta">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden bg-[#F5F0E8] border border-border/60"
        >
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231A3626' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
          <div className="relative flex flex-col md:flex-row items-center gap-8 p-8 sm:p-12 lg:p-16">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-accent/15 text-accent px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles size={14} /> New
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-primary leading-snug">
                What's your decor<br className="hidden sm:block" /> personality?
              </h2>
              <p className="mt-3 text-muted-foreground text-sm sm:text-base max-w-md leading-relaxed">
                Take our 2-minute style quiz and discover pieces curated just for your taste.
              </p>
              <Link
                to="/quiz"
                className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide hover:gap-3 transition-all"
                data-testid="home-quiz-btn"
              >
                Take the Style Quiz <ArrowRight size={18} />
              </Link>
            </div>
            <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full bg-primary/5 border-2 border-primary/10 flex items-center justify-center shrink-0">
              <div className="text-center">
                <Sparkles size={32} className="mx-auto text-accent mb-2" />
                <p className="font-display text-lg text-primary">5 Questions</p>
                <p className="text-xs text-muted-foreground">Personalised picks</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Editorial */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
          <div className="bg-primary text-primary-foreground p-10 lg:p-16 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Our promise</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium leading-tight">
              Sourced with care,<br />finished with intent.
            </h2>
            <p className="mt-6 text-primary-foreground/80 font-light leading-relaxed max-w-md">
              Every set we resell is chosen for how it feels in the hand and how
              it settles into a room. From mango-wood trays to 210-thread-count
              cotton bedding, we keep things honest, tactile and beautifully
              simple.
            </p>
            <Link
              to="/shop"
              className="mt-9 inline-flex items-center gap-2 bg-[#FAFAF7] text-primary px-8 py-4 text-sm tracking-wide w-fit hover:gap-3 transition-all"
            >
              Explore everything <ArrowRight size={18} />
            </Link>
          </div>
          <div className="min-h-[360px] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1782718979595-be6003fc498e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Styled interior"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Instagram */}
      <InstaFeed />
    </div>
  );
}
